/**
 * useFormularioDFD — estado do formulário do DFD: valores, autosave
 * debounced, sugestões de IA e as regras condicionais (R1-R9 do template
 * original, agora alimentadas pela configuração que vem do backend).
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  carregarRascunho,
  configuracaoDFD,
  salvarRascunho,
  sugerirCampo,
  type FormDataDFD,
} from '../../api/dfd'
import type { EstadoPlanilha } from './PlanilhaItens'

const VALORES_INICIAIS: FormDataDFD = {
  tipo_objeto: 'bem_comum',
  tipo_contratacao_direta: 'licitacao',
  orcamento_sigiloso: 'publico',
  tipo_contratacao: 'material',
  modalidade_licitacao: 'pregao',
  hipotese_credenciamento: 'paralela',
}

/**
 * Opções compatíveis com cada hipótese do Art. 79 — espelho de
 * services/hipoteses_legais.py (conflito_credenciamento e
 * conflito_remuneracao_credenciamento), que a rota /dfd/create aplica como
 * cerca final. tests/test_tr_prompts_por_regime.py confere o espelho.
 * - distribuição: livre escolha do beneficiário É o inciso II (§1º, II
 *   trata da distribuição no inciso I);
 * - remuneração: nos incisos I e II o edital define o valor (§1º, III); no
 *   III vale a cotação de mercado no momento da contratação (§1º, IV).
 */
export const OPCOES_ART_79: Record<string, { distribuicao: string[]; remuneracao: string[] }> = {
  paralela: { distribuicao: ['rodizio', 'ordem_demanda'], remuneracao: ['tabela_administracao', 'tabela_referencial'] },
  terceiros: { distribuicao: ['livre_escolha_beneficiario'], remuneracao: ['tabela_administracao', 'tabela_referencial'] },
  mercados_fluidos: { distribuicao: ['rodizio', 'ordem_demanda'], remuneracao: ['preco_mercado'] },
}

/** Regra da hipótese; sem hipótese conhecida, a do inciso I (o default do formulário). */
export function opcoesArt79(hipotese: string | undefined) {
  return OPCOES_ART_79[hipotese ?? 'paralela'] ?? {
    distribuicao: ['rodizio', 'ordem_demanda'],
    remuneracao: ['tabela_administracao', 'tabela_referencial'],
  }
}

const ATRASO_AUTOSAVE_MS = 2000

export type EstadoSalvamento = 'ocioso' | 'salvando' | 'salvo' | 'erro'

export function useFormularioDFD(licitacaoIdInicial: number | null) {
  const [valores, setValores] = useState<FormDataDFD>(VALORES_INICIAIS)
  const [licitacaoId, setLicitacaoId] = useState<number | null>(licitacaoIdInicial)
  const [erros, setErros] = useState<Record<string, string>>({})
  const [salvamento, setSalvamento] = useState<EstadoSalvamento>('ocioso')
  const [sugestoesAtivas, setSugestoesAtivas] = useState(true)
  const [campoSugerindo, setCampoSugerindo] = useState<string | null>(null)
  const [planilhaMaterial, setPlanilhaMaterial] = useState<EstadoPlanilha | null>(null)
  const [planilhaServico, setPlanilhaServico] = useState<EstadoPlanilha | null>(null)
  const temporizador = useRef<number | null>(null)
  const sugeridos = useRef<Set<string>>(new Set())

  const { data: configuracao } = useQuery({
    queryKey: ['dfd-configuracao'],
    queryFn: configuracaoDFD,
    staleTime: Infinity, // regras da lei não mudam durante a sessão
  })

  // Município da prefeitura logada pré-preenche o campo (paridade com o
  // Jinja). Sem isso, um usuário logado numa prefeitura gerou o DFD
  // inteiro citando OUTRO município — o valor digitado viaja para todos
  // os prompts. Só preenche campo vazio: rascunho e digitação vencem.
  useEffect(() => {
    const municipio = configuracao?.municipio
    if (!municipio) return
    setValores((atuais) =>
      atuais.municipio?.trim() ? atuais : { ...atuais, municipio },
    )
  }, [configuracao])

  // Rascunho existente (?licitacao_id= na URL)
  const { data: rascunho, isLoading: carregandoRascunho } = useQuery({
    queryKey: ['dfd-rascunho', licitacaoIdInicial],
    queryFn: () => carregarRascunho(licitacaoIdInicial!),
    enabled: licitacaoIdInicial !== null,
    retry: false,
  })

  useEffect(() => {
    if (!rascunho?.form_data) return
    const dados = rascunho.form_data
    setValores((atuais) => ({ ...atuais, ...dados }))
    setLicitacaoId(rascunho.licitacao_id)

    // repõe as tabelas de itens salvas no rascunho
    const repor = (
      chaveItens: string,
      chaveTotal: string,
      aplicar: (e: EstadoPlanilha) => void,
    ) => {
      if (!dados[chaveItens]) return
      try {
        const itens = JSON.parse(dados[chaveItens])
        if (Array.isArray(itens) && itens.length > 0) {
          aplicar({ itens, total: Number(dados[chaveTotal]) || itens.length })
        }
      } catch {
        /* rascunho antigo com formato inesperado: ignora a tabela */
      }
    }
    repor('planilha_items', 'planilha_total', setPlanilhaMaterial)
    repor('planilha_servico_items', 'planilha_servico_total', setPlanilhaServico)
  }, [rascunho])

  /** O rascunho leva os itens da planilha nas MESMAS chaves que o
   *  create_dfd legado lê (planilha_items / planilha_servico_items). */
  const comPlanilhas = useCallback((): FormDataDFD => {
    const dados: FormDataDFD = { ...valores }
    if (planilhaMaterial) {
      dados.planilha_material = 'planilha_salva'
      dados.planilha_items = JSON.stringify(planilhaMaterial.itens)
      dados.planilha_total = String(planilhaMaterial.total)
    }
    if (planilhaServico) {
      dados.planilha_servico = 'planilha_salva'
      dados.planilha_servico_items = JSON.stringify(planilhaServico.itens)
      dados.planilha_servico_total = String(planilhaServico.total)
    }
    return dados
  }, [valores, planilhaMaterial, planilhaServico])

  const salvar = useMutation({
    mutationFn: () => salvarRascunho(comPlanilhas(), licitacaoId),
    onMutate: () => setSalvamento('salvando'),
    onSuccess: (dados) => {
      setLicitacaoId(dados.licitacao_id)
      setSalvamento('salvo')
      window.setTimeout(() => setSalvamento('ocioso'), 3000)
    },
    onError: () => setSalvamento('erro'),
  })

  const agendarAutosave = useCallback(() => {
    if (temporizador.current) window.clearTimeout(temporizador.current)
    temporizador.current = window.setTimeout(
      () => salvar.mutate(),
      ATRASO_AUTOSAVE_MS,
    )
  }, [salvar])

  useEffect(
    () => () => {
      if (temporizador.current) window.clearTimeout(temporizador.current)
    },
    [],
  )

  const definir = useCallback((campo: string, valor: string) => {
    setValores((atuais) => ({ ...atuais, [campo]: valor }))
    setErros((atuais) => {
      if (!atuais[campo]) return atuais
      const proximos = { ...atuais }
      delete proximos[campo]
      return proximos
    })
  }, [])

  /** Sugestão de IA ao focar um campo VAZIO (comportamento do original). */
  const sugerir = useCallback(
    async (campo: string) => {
      if (!sugestoesAtivas) return
      if (valores[campo]?.trim()) return
      if (sugeridos.current.has(campo)) return // uma vez por campo, como no legado
      const objeto = valores.objeto_contratacao?.trim()
      if (!objeto) return

      sugeridos.current.add(campo)
      setCampoSugerindo(campo)
      try {
        const { sugestao } = await sugerirCampo(
          campo,
          objeto,
          valores.orgao_solicitante ?? '',
          {
            tipo_contratacao_direta: valores.tipo_contratacao_direta ?? '',
            hipotese_dispensa: valores.hipotese_dispensa ?? '',
            inciso_dispensa: valores.inciso_dispensa ?? '',
            hipotese_inexigibilidade: valores.hipotese_inexigibilidade ?? '',
            hipotese_credenciamento: valores.hipotese_credenciamento ?? '',
            consta_pca: valores.consta_pca ?? '',
          },
        )
        // só preenche se o usuário não digitou nada nesse meio-tempo
        setValores((atuais) =>
          atuais[campo]?.trim() ? atuais : { ...atuais, [campo]: sugestao },
        )
      } catch {
        sugeridos.current.delete(campo) // permite nova tentativa
      } finally {
        setCampoSugerindo(null)
      }
    },
    [sugestoesAtivas, valores],
  )

  // ── Regras condicionais ────────────────────────────────────────────────
  const regra = useMemo(() => {
    const tipoObjeto = valores.tipo_objeto ?? 'bem_comum'
    const comum = valores.servico_comum_engenharia === 'on'
    const chave =
      tipoObjeto === 'obra_engenharia' && comum
        ? 'servico_comum_engenharia'
        : tipoObjeto
    return configuracao?.modalidades_por_tipo_objeto[chave]
  }, [configuracao, valores.tipo_objeto, valores.servico_comum_engenharia])

  const contratacaoDireta =
    valores.tipo_contratacao_direta === 'inexigibilidade' ||
    valores.tipo_contratacao_direta === 'dispensa'

  const visibilidade = {
    // R1: obra de engenharia abre o checkbox do Art. 6º, XXI "a"
    servicoComumEngenharia: valores.tipo_objeto === 'obra_engenharia',
    // R4: contratação direta desabilita modalidade, orçamento e SRP
    contratacaoDireta,
    inexigibilidade: valores.tipo_contratacao_direta === 'inexigibilidade',
    dispensa: valores.tipo_contratacao_direta === 'dispensa',
    // R5: credenciamento (Art. 74, IV → Art. 79)
    credenciamento:
      valores.tipo_contratacao_direta === 'inexigibilidade' &&
      valores.hipotese_inexigibilidade === 'credenciamento',
    // R6: tabela referencial só quando a origem é referencial
    referencialTabela: valores.origem_tabela_precos === 'tabela_referencial',
    // R8: inciso livre só em "outra"; aviso de emergência
    incisoDispensa: valores.hipotese_dispensa === 'outra',
    avisoEmergencia: valores.hipotese_dispensa === 'emergencia',
    // R9: sigilo exige justificativa (Art. 18, §1º, VI)
    sigilo: valores.orcamento_sigiloso === 'sigiloso',
    // R3: modalidade diferente da principal exige justificativa
    justificativaModalidade:
      !contratacaoDireta &&
      !!regra &&
      valores.modalidade_licitacao !== regra.principal,
    // modalidade obrigatória = sem alternativas
    modalidadeObrigatoria: !!regra && regra.alternativas.length === 0,
    permiteSrp: !contratacaoDireta && !!regra?.permite_srp,
    // R14: fora do PCA exige justificativa
    justificativaPca: valores.consta_pca === 'false',
    vinculoPca: valores.consta_pca === 'true',
  }

  // R2: trocar o tipo de objeto realinha a modalidade com a principal
  useEffect(() => {
    if (!regra) return
    setValores((atuais) => {
      const permitidas = [regra.principal, ...regra.alternativas]
      if (atuais.modalidade_licitacao &&
          permitidas.includes(atuais.modalidade_licitacao)) {
        return atuais
      }
      return { ...atuais, modalidade_licitacao: regra.principal }
    })
  }, [regra])

  // R7: a hipótese do art. 79 restringe distribuição e remuneração
  // (OPCOES_ART_79). Depende TAMBÉM dos dois campos: só reagir à troca da
  // hipótese deixava o usuário mudar o select depois — o DFD 1640 saiu com
  // "terceiros" + rodízio, e o 1651 com "paralela" + livre escolha.
  // (O select filtra as opções na UI e o backend valida; isto é a 3ª cerca.)
  useEffect(() => {
    const regra = opcoesArt79(valores.hipotese_credenciamento)
    setValores((atuais) => {
      const ajustar = (atual: string | undefined, permitidas: string[]): string =>
        atual && permitidas.includes(atual) ? atual
          : permitidas.length === 1 ? (permitidas[0] ?? '') : ''
      const distribuicao = ajustar(atuais.forma_distribuicao_demanda, regra.distribuicao)
      const remuneracao = atuais.origem_tabela_precos
        ? ajustar(atuais.origem_tabela_precos, regra.remuneracao)
        : (regra.remuneracao.length === 1 ? (regra.remuneracao[0] ?? '') : (atuais.origem_tabela_precos ?? ''))
      return distribuicao === atuais.forma_distribuicao_demanda
        && remuneracao === atuais.origem_tabela_precos
        ? atuais
        : { ...atuais, forma_distribuicao_demanda: distribuicao, origem_tabela_precos: remuneracao }
    })
  }, [valores.hipotese_credenciamento, valores.forma_distribuicao_demanda,
      valores.origem_tabela_precos])

  /** Valida no cliente e, se ok, salva o rascunho e submete ao endpoint
   *  legado de geração (POST /dfd/create) via form nativo — as 814 linhas
   *  que montam o DFDData e disparam a task Celery seguem intactas. */
  const gerar = useCallback(async (): Promise<string | null> => {
    const obrigatorios = configuracao?.campos_obrigatorios ?? {}
    const faltando: Record<string, string> = {}
    for (const [campo, rotulo] of Object.entries(obrigatorios)) {
      if (!valores[campo]?.trim()) faltando[campo] = `${rotulo} é obrigatório.`
    }
    // R14 também na submissão: fora do PCA sem justificativa a rota recusa
    if (valores.consta_pca === 'false' && !valores.justificativa_pca?.trim()) {
      faltando.justificativa_pca =
        'Justifique por que a contratação não consta no PCA.'
    }
    if (!planilhaMaterial && !planilhaServico) {
      faltando._planilha =
        'Anexe pelo menos uma planilha (Materiais ou Serviços) para prosseguir.'
    }
    if (Object.keys(faltando).length > 0) {
      setErros(faltando)
      return faltando._planilha ?? 'Preencha os campos obrigatórios destacados.'
    }

    const dados = comPlanilhas()
    const salvo = await salvarRascunho(dados, licitacaoId)

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/dfd/create'
    form.enctype = 'multipart/form-data'
    const anexar = (nome: string, valor: string) => {
      const campo = document.createElement('input')
      campo.type = 'hidden'
      campo.name = nome
      campo.value = valor
      form.appendChild(campo)
    }
    Object.entries(dados).forEach(([nome, valor]) => anexar(nome, valor ?? ''))
    anexar('licitacao_id', String(salvo.licitacao_id))
    // o wrapper de CSRF do Jinja não existe aqui: token vai no próprio form
    const token = document.cookie
      .split('; ')
      .find((c) => c.startsWith('XSRF-TOKEN='))
      ?.slice('XSRF-TOKEN='.length)
    if (token) anexar('csrf_token', decodeURIComponent(token))

    document.body.appendChild(form)
    form.submit()
    return null
  }, [configuracao, valores, planilhaMaterial, planilhaServico, comPlanilhas,
      licitacaoId])

  return {
    configuracao,
    valores,
    definir,
    erros,
    setErros,
    licitacaoId,
    salvamento,
    salvarAgora: () => salvar.mutate(),
    agendarAutosave,
    planilhaMaterial,
    definirPlanilhaMaterial: setPlanilhaMaterial,
    planilhaServico,
    definirPlanilhaServico: setPlanilhaServico,
    gerar,
    carregandoRascunho,
    sugestoesAtivas,
    alternarSugestoes: () => setSugestoesAtivas((v) => !v),
    sugerir,
    campoSugerindo,
    regra,
    visibilidade,
  }
}
